import { Link, useRouterState } from "@tanstack/react-router";
import clsx from "clsx";
import { PenLine, Calendar, Users, MessagesSquare, Clock } from "lucide-react";
import { CountBadge, Tooltip, theme } from "@dfws/ui";
import { ROLE_ICON, folderLabel, folderSlug } from "./nav";
import { useMailboxes } from "../lib/queries";
import { totalUnread, useChannels } from "../features/chat/chatData";
import { newCompose, useOutbox } from "../features/compose/composeData";
import { Skeleton } from "@dfws/ui";
import s from "./Sidebar.module.css";

export function Sidebar({ collapsed, onNavigate }: { collapsed: boolean; onNavigate?: () => void }) {
  const path = useRouterState({ select: (st) => st.location.pathname });
  const mailboxes = useMailboxes();
  const channels = useChannels();
  const outbox = useOutbox();
  const scheduled = (outbox.data?.items ?? []).filter((o) => o.status === "queued" || o.status === "failed").length;
  const item = (to: string, label: string, icon: React.ReactNode, count?: number) => {
    const active = path === to || (to !== "/" && path.startsWith(to + "/"));
    const link = (
      <Link to={to} className={clsx(s.item, active && s.active)} aria-current={active ? "page" : undefined} onClick={onNavigate}>
        <span className={s.icon}>{icon}</span>
        <span className={s.text}>{label}</span>
        {count ? <CountBadge count={count} emphasis={active || label === "Inbox"} className={s.count} /> : null}
      </Link>
    );
    return collapsed ? (
      <Tooltip key={to} content={count ? `${label} (${count})` : label} side="right">
        {link}
      </Tooltip>
    ) : (
      <div key={to}>{link}</div>
    );
  };

  return (
    <nav className={clsx(s.sidebar, collapsed && s.collapsed)} aria-label="Main">
      <div className={s.brand}>
        <img className={s.mark} src={theme.logo.mark} alt="" width={28} height={28} />
        {!collapsed && <span className={s.wordmark}>Workspace</span>}
      </div>
      <Tooltip content="Compose (c)" side="right">
        <span className={s.composeWrap}>
          <button type="button" className={s.compose} aria-label="Compose" aria-keyshortcuts="c" onClick={() => { onNavigate?.(); void newCompose(); }}>
            <PenLine />
            {!collapsed && <span>Compose</span>}
          </button>
        </span>
      </Tooltip>
      <div className={s.scroll}>
        <div className={s.group}>
          {mailboxes.data?.items.length
            ? mailboxes.data.items.map((m) => {
                const Icon = ROLE_ICON[m.role];
                // Unread counts matter for Inbox and your own folders; Spam and Trash show none, like Gmail.
                const count = m.role === "junk" || m.role === "trash" || m.role === "sent" ? 0 : m.role === "drafts" ? m.total : m.unread;
                return item(`/mail/${folderSlug(m)}`, folderLabel(m), <Icon />, count);
              })
            : Array.from({ length: 6 }, (_, i) => (
                <div key={i} className={s.skeletonRow} aria-hidden>
                  <Skeleton width={18} height={18} radius={4} />
                  {!collapsed && <Skeleton width={`${50 + ((i * 17) % 40)}%`} height={12} />}
                </div>
              ))}
        </div>
        <div className={s.group}>
          {scheduled > 0 && item("/scheduled", "Scheduled", <Clock />, scheduled)}
          {item("/chat", "Chat", <MessagesSquare />, totalUnread(channels.data?.items))}
          {item("/calendar", "Calendar", <Calendar />)}
          {item("/contacts", "Contacts", <Users />)}
        </div>
      </div>
    </nav>
  );
}
